import type { MessageResponse } from '@/domain/Api';
import { defaultAddressKey } from '@/domain/User';
import { api } from '@/lib/api';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import { queryKeys } from '../queryKeys';

export interface CreateAddressRequest {
  name?: string;
  country: string;
  city: string;
  line1: string;
  line2?: string | null;
  zipcode: string;
}

export type UpdateAddressRequest = CreateAddressRequest & { name: string };

export const createAddressMutation = (customApi: typeof api = api) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationFn: async (values: CreateAddressRequest) =>
      customApi
        .post('addresses', {
          json: { ...values, name: values.name ?? defaultAddressKey },
        })
        .json<void>(),
    onSuccess: (_data, _variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.current(),
      });
    },
  });
};

export const updateAddressMutation = (addressId: number) => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.addresses.update(addressId),
    mutationFn: async (values: UpdateAddressRequest) =>
      api
        .put(`addresses/${addressId}`, {
          json: values,
        })
        .json<MessageResponse>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.user.current() });
      toast.success('Address changed successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update address');
    },
  });
};
